import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { WelletContext } from '../../context/WelletContext';
import { getQueryString } from '../../helpers/url';
import { fetchPickupDetails, fetchPickups } from '../../services/reservations/reservationsService';
import Header from '../../components/Common/Header/Header';
import ContentApp from '../../components/ContentApp';
import GeneralSelectorPicker from '../../components/SelectorPicker/GeneralSelectorPicker';
import Money from '../../components/Common/Money/Money';
import ButtonReservation from '../../components/Common/Button/ButtonReservation';
import { resetTransportation, setTransportation } from '../../actions/reservationActions';
import { capitalizedWords } from '../../helpers/text';
import { sortByField } from '../../helpers/array';
import ChevronIcon from '../../components/icons/chevronIcon';
import { Skeleton } from '@mui/material';
import ErrorBox from '../../components/Common/ErrorBox/ErrorBox';

const Pickup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);
  const history = useHistory();
  const showId = getQueryString('showId');
  const eventId = getQueryString('eventId');
  const reservation = useSelector((state) => state.reservation);
  const [loading, setLoading] = useState(false);
  const [withTransport, setWithTransport] = useState(false);
  const [zones, setZones] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [pickupsFilter, setPickupsFilter] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedPickup, setSelectedPickup] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [pickupInfo, setPickupInfo] = useState(null);
  const [showCustomPickup, setShowCustomPickup] = useState(false);
  const [customPickup, setCustomPickup] = useState('');
  const [error, setError] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const getPickup = async () => {
    setLoading(true);

    try {
      const pickup = await fetchPickups(eventId, 'TICKET', welletContext);
      setZones(
        [...new Set(pickup.map((item) => item.description))].map((desc) => {
          const pickupMatch = pickup.find((item) => item.description === desc);
          return {
            value: desc,
            label: desc === 'CUSTOM' ? t('customPickup') : capitalizedWords(desc),
            id: pickupMatch?.id,
          };
        }),
      );

      setPickups(pickup);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPickupDetails = async (name, pickupId) => {
    setLoadingDetails(true);
    try {
      const result = await fetchPickupDetails(
        eventId,
        'TICKET',
        name,
        reservation.performance,
        welletContext,
        pickupId,
      );
      setPickupInfo(result);
      setSelectedPrice(result);
    } catch (error) {
      console.error(error);
      setPickupInfo(null);
      setSelectedPrice(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const getTotal = () => {
    if (!selectedPrice) return 0;
    return selectedPrice.price * reservation.paxs;
  };

  const handleZoneSelect = (zone) => {
    setSelectedZone(zone);
    setSelectedPickup(null);
    setSelectedPrice(null);
    if (zone.value === 'CUSTOM') {
      return handleSelectCustom(pickups.filter((p) => p.description === zone.value)[0]);
    } else {
      setShowCustomPickup(false);
      setCustomPickup(null);
    }

    const filteredPickups = pickups.filter((p) => p.description === zone.value);

    setPickupsFilter(
      filteredPickups.map((p) => ({
        value: p.name,
        label: capitalizedWords(p.name),
        id: p.id,
      })),
    );

    setError(false);
  };

  const handleSelectCustom = (item) => {
    setShowCustomPickup(true);
    setSelectedPickup({ value: item.name, label: item.name });
    getPickupDetails(item.name, item.id);
  };

  const handlePickupSelect = (p) => {
    setSelectedPickup(p);
    setSelectedPrice(null);
    if (p?.id) {
      getPickupDetails(p.label, p.id);
    } else {
      const match = pickups.find((pickup) => pickup.name === p.value);
      if (match) getPickupDetails(p.label, match.id);
    }

    setError(false);
  };

  const handlePriceSelect = (pickupInfo) => {
    if (pickupInfo) {
      setSelectedPrice({ price: pickupInfo.price });
      setError(false);
    }
  };

  const isTransportFormComplete = () => {
    if (!withTransport) return true;
    if (showCustomPickup) return customPickup.length > 0;
    return selectedZone && selectedPickup && selectedPrice;
  };

  const handleContinue = () => {
    if (withTransport) {
      const isComplete = isTransportFormComplete();
      if (!isComplete) {
        setError(true);
        return;
      }

      dispatch(
        setTransportation({
          id: pickupInfo.id,
          name: selectedPickup.label,
          description: showCustomPickup ? customPickup : selectedZone.label,
          time: pickupInfo?.time,
          type: 'Pick-up standard',
          price: pickupInfo?.price,
        }),
      );
    }

    let query = `?showId=${showId}&eventId=${eventId}${
      pickupInfo?.id ? `&pickupId=${pickupInfo.id}` : ''
    }`;

    history.push({
      pathname: '/reservation-check-tickets',
      search: query,
    });
  };

  const renderCustomPickup = () => {
    return (
      <div className='mt-2'>
        <div className='fw-semibold text-primary font-tiny mt-3 mb-1 ml-1'>
          {t('wherePickupCustom')}
        </div>
        <div className={`field-holder ${error ? 'error' : ''}`}>
          <div className='input-placeholder'>
            <textarea
              onChange={(e) => {
                setCustomPickup(e.target.value);
                setError(false);
              }}
            >
              {customPickup}
            </textarea>
            <span className='floating-placeholder '>{t('addressOrReference')}</span>
          </div>
          {error && (
            <div
              className='error-label text-center'
              style={{ position: 'relative', marginTop: '10px' }}
            >
              {t('customPickupError')}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPickupSelector = () => {
    return (
      <>
        {selectedZone && (
          <>
            <div className='fw-semibold text-primary font-tiny mt-3 mb-1 ml-1'>
              {t('pickupLocation')}
            </div>

            <GeneralSelectorPicker
              key={pickupsFilter[0]?.value}
              options={sortByField(pickupsFilter, 'label')}
              selectedValue={selectedPickup}
              onChange={handlePickupSelect}
              placeholder={t('select')}
            />
          </>
        )}

        {selectedPickup && (
          <>
            <div className='fw-semibold text-primary font-tiny mt-4'>
              {t('schedulesTransportation')}
            </div>
            {loadingDetails ? (
              <div className='pt-2'>
                <Skeleton
                  variant='rectangular'
                  sx={{
                    bgcolor: 'grey.900',
                    width: '100%',
                    borderRadius: '.4rem',
                    height: 88,
                  }}
                />
              </div>
            ) : pickupInfo === null ? (
              <div className='mt-2'>
                <ErrorBox title={t('pickupNotAvailable')} />
              </div>
            ) : (
              <div
                className={`btn-primary selector w-100 text-left line-height-1 py-2 mt-2 p-2  ${
                  !selectedPrice ? 'btn-outline' : ''
                }`}
                style={{ height: 'auto', borderRadius: '0.25rem' }}
                onClick={() => handlePriceSelect(pickupInfo)}
              >
                <div className='ml-1'>
                  <div className='fw-semibold font-normal'>{t('standardpickup')}</div>
                  <div className='fw-300 font-small mt-1'>
                    <div className='fw-300 font-small mt-1'>
                      {pickupInfo?.time}{' '}
                      {parseInt(pickupInfo?.time?.split(':')[0], 10) < 12 ? 'am' : 'pm'} -{' '}
                      {t('productType.EXTRAPAX.priceUnit')}
                    </div>
                  </div>
                  <div className='fw-semibold font-normal mt-3'>
                    {pickupInfo.price > 0 ? <Money value={pickupInfo.price} /> : t('included')}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {error && (
          <div
            className='error-label text-center'
            style={{ position: 'relative', marginTop: '10px' }}
          >
            {t('selectAnOption')}
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    dispatch(resetTransportation());
  }, []);

  useEffect(() => {
    if (withTransport && pickups.length === 0) getPickup();
  }, [withTransport]);

  return (
    <>
      <>
        <Header />
        <ContentApp classes='pb-0'>
          <div className='fw-semibold text-primary font-tiny'>{t('transportInformation')}</div>
          <div className='row mt-3'>
            <div className='col pr-1'>
              <button
                style={{ lineHeight: '20px' }}
                className={`btn btn-primary selector w-100 text-left font-tiny ${
                  withTransport ? ' btn-outline' : ''
                }`}
                onClick={() => setWithTransport(false)}
              >
                {t('withoutTransportation')}
              </button>
            </div>
            <div className='col pl-1'>
              <button
                className={`btn btn-primary selector w-100 text-left font-tiny ${
                  !withTransport ? ' btn-outline' : ''
                }`}
                onClick={() => setWithTransport(true)}
              >
                {t('withTransportation')}
              </button>
            </div>
          </div>

          {withTransport && (
            <div className='card py-3 px-2 mt-2'>
              <div className='fw-semibold text-primary font-tiny mb-1 ml-1'>{t('zona')}</div>
              {loading && !selectedZone ? (
                <Skeleton
                  variant='rectangular'
                  sx={{
                    bgcolor: 'grey.900',
                    width: '100%',
                    borderRadius: '.4rem',
                    height: 61,
                  }}
                />
              ) : (
                <GeneralSelectorPicker
                  options={sortByField(zones, 'label')}
                  onChange={handleZoneSelect}
                  placeholder={t('select')}
                  selectedValue={selectedZone}
                />
              )}

              {showCustomPickup ? renderCustomPickup() : renderPickupSelector()}
            </div>
          )}
          <div
            className='position-fixed w-100'
            style={{
              bottom: 0,
              left: 0,
              zIndex: 2,
            }}
          >
            <div className='container'>
              <div className='row justify-content-center'>
                <div className='col-12 col-md-7 col-lg-6 p-0'>
                  <ButtonReservation
                    leftContent={
                      withTransport && selectedPrice ? (
                        <>
                          {t('total')} <Money value={getTotal()} />
                        </>
                      ) : (
                        t('continue')
                      )
                    }
                    rightContent={<ChevronIcon size={20} fillColor={'#000'} />}
                    backgroundColor={
                      withTransport
                        ? isTransportFormComplete()
                          ? 'var(--color-primary)'
                          : 'var(--color-deep-gray)'
                        : 'var(--color-primary)'
                    }
                    size='large'
                    className='btn-primary'
                    onClick={handleContinue}
                    fixed={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </ContentApp>
      </>
    </>
  );
};

export default Pickup;
